import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal
} from "@angular/core";
import {rxResource, takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormGroupDirective} from "@angular/forms";
import {sendQuery} from "../../../../../common/utils";
import {
  DeclarationStatus,
  HarbourDuesDeclaration,
  HarbourDuesRotterdamProformaResponse
} from "@portbase/bezoekschip-service-typescriptmodels";
import {HarbourDuesUtilsService} from "../../../services/harbour-dues-utils.service";

@Component({
  selector: "app-harbour-dues-rotterdam-pro-forma",
  templateUrl: "./harbour-dues-rotterdam-pro-forma.component.html",
  styleUrl: "./harbour-dues-rotterdam-pro-forma.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HarbourDuesRotterdamProFormaComponent {
  private readonly form = inject(FormGroupDirective);
  private readonly destroyRef = inject(DestroyRef);
  private readonly harbourDuesUtilService = inject(HarbourDuesUtilsService);

  readonly selectedDeclaration = input.required<HarbourDuesDeclaration>();

  readonly proFormaRequested = output<void>();
  readonly validProFormaDisplayed = output<boolean>();

  protected readonly shouldReload = signal(false);
  protected readonly inputDeclaration = signal<HarbourDuesDeclaration & {cacheBuster: Date} | null>(null);
  protected readonly proFormaResource = rxResource<HarbourDuesRotterdamProformaResponse, HarbourDuesDeclaration | null>({
    params: () => this.inputDeclaration(),
    stream: ({ params }) => sendQuery(
      "com.portbase.bezoekschip.common.api.harbourdues.queries.GetRotterdamHarbourDuesProforma",
      params
    )
  });

  protected readonly isDeclared = computed(
    () => this.selectedDeclaration()?.status === DeclarationStatus.DECLARED
  );

  constructor() {
    effect(() => {
      this.validProFormaDisplayed.emit(
        this.proFormaResource.hasValue() && !this.shouldReload()
      );
    });
  }

  ngOnInit(): void {
    const formGroup = this.form.control;
    formGroup.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      () => this.shouldReload.set(true)
    );
  }

  requestProForma(): void {
    this.proFormaRequested.emit();
    if (this.form.valid) {
      const mergedDeclaration = {
        ...this.harbourDuesUtilService.mergeDeclarationAndFormData(
          this.selectedDeclaration(),
          this.form.control.value
        ),
        cacheBuster: new Date()
      };
      this.inputDeclaration.set(mergedDeclaration);
      this.shouldReload.set(false);
    }
  }
}
